// components/Modals/BillItemModal.jsx
import React, { useEffect, useRef, useState } from "react";
import {
  Drawer,
  Button,
  Spin,
  Descriptions,
  Table,
  Divider,
  Space,
  Tag,
  Typography,
} from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import "antd/dist/reset.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { VATInvoiceA4 } from "../Templates/vatemplate";
import fetchData from "../../functions/fetchData";

const { Text } = Typography;

export default function BillItemModal({ isOpen, onClose, bill }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const printRef = useRef();

  useEffect(() => {
    if (bill?.id && isOpen) {
      fetchItems();
    }
  }, [bill, isOpen]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      let result = await fetchData("bill_items", null, "id", {
        order_id: bill.id,
      });

      if (!Array.isArray(result) || result.length === 0) {
        result = await fetchData("bill_items", null, "id", {
          invoice_number: bill.bill_number,
        });
      }

      if (!Array.isArray(result) || result.length === 0) {
        result = await fetchData("bill_items", null, "id", {
          bill_id: bill.id,
        });
      }

      setItems(Array.isArray(result) ? result : []);
    } catch (error) {
      toast.error("Failed to fetch bill items");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printContents = printRef.current.innerHTML;
    const win = window.open("", "_blank");
    win.document.write(
      `<html><head><title>Invoice - ${bill?.bill_number || ""}</title></head><body>${printContents}</body></html>`
    );
    win.document.close();
    win.focus();
    win.print();
    win.close();
  };

  // ── VAT template data ──────────────────────────────────────────────────
  const company = {
    name: "ChefMate Restaurant",
    address: "Your Restaurant Address, City, State, ZIP",
    phone: "+123-456-7890",
    email: "info@chefmate.com",
    tax_id: "123456789012345",
    currency: "THB",
    developer: "Chefmate POS-+66986643299/+66952477020",
  };

  const customer = {
    name: bill?.customer_name || "Walk-in Customer",
    phone: bill?.customer_phone || "N/A",
    email: "N/A",
    address: "N/A",
    vat: "N/A",
  };

  const vatItems = items.map((item) => ({
    item_name: item.item_name,
    quantity: item.quantity,
    total_price: parseFloat(item.total_price || 0),
  }));

  const summary = {
    subtotal: parseFloat(bill?.subtotal || 0).toFixed(2),
    discount: parseFloat(bill?.discount_amount || 0).toFixed(2),
    subtotalAfterDiscount: parseFloat(bill?.subtotal || 0).toFixed(2),
    payment: parseFloat(bill?.tax_amount || 0).toFixed(2),
    roundoff: "0.00",
    grandTotal: parseFloat(bill?.total_amount || 0).toFixed(2),
  };

  // ── Items table columns ────────────────────────────────────────────────
  const itemColumns = [
    {
      title: "#",
      key: "idx",
      width: 40,
      render: (_, __, i) => i + 1,
    },
    {
      title: "Item",
      dataIndex: "item_name",
      key: "item_name",
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      align: "right",
      width: 60,
    },
    {
      title: "Total (฿)",
      dataIndex: "total_price",
      key: "total_price",
      align: "right",
      width: 110,
      render: (val) => parseFloat(val || 0).toFixed(2),
    },
  ];

  return (
    <>
      <Drawer
        title={
          <Space>
            <Text strong>Bill Details</Text>
            {bill?.bill_number && (
              <Tag color="blue">{bill.bill_number}</Tag>
            )}
            {bill?.status && (
              <Tag color={bill.status === "active" ? "green" : "red"}>
                {bill.status.toUpperCase()}
              </Tag>
            )}
          </Space>
        }
        placement="right"
        width={640}
        open={isOpen}
        onClose={onClose}
        extra={
          <Button
            icon={<PrinterOutlined />}
            type="primary"
            onClick={handlePrint}
          >
            Print
          </Button>
        }
        destroyOnClose
      >
        <Spin spinning={loading} tip="Loading bill details...">
          {/* ── Bill summary ── */}
          <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Bill No.">
              {bill?.bill_number || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Date">
              {bill?.setup_date || bill?.bill_date || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Time">
              {bill?.bill_time || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Cashier ID">
              {bill?.cashier_id || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Customer" span={2}>
              {bill?.customer_name || "Walk-in Customer"}
            </Descriptions.Item>
            <Descriptions.Item label="Payment Method" span={2}>
              <Tag color="blue">
                {(bill?.payment_method || "-").toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Subtotal">
              ฿{parseFloat(bill?.subtotal || 0).toFixed(2)}
            </Descriptions.Item>
            <Descriptions.Item label="Tax">
              ฿{parseFloat(bill?.tax_amount || 0).toFixed(2)}
            </Descriptions.Item>
            <Descriptions.Item label="Discount">
              ฿{parseFloat(bill?.discount_amount || 0).toFixed(2)}
            </Descriptions.Item>
            <Descriptions.Item label="Total">
              <Text strong style={{ color: "#1890ff", fontSize: 15 }}>
                ฿{parseFloat(bill?.total_amount || 0).toFixed(2)}
              </Text>
            </Descriptions.Item>
          </Descriptions>

          <Divider orientation="left">Items</Divider>

          {/* ── Items table ── */}
          <Table
            rowKey={(_, i) => i}
            dataSource={items}
            columns={itemColumns}
            size="small"
            pagination={false}
            summary={() => (
              <Table.Summary>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={3} align="right">
                    <Text strong>Total</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">
                    <Text strong style={{ color: "#1890ff" }}>
                      ฿
                      {items
                        .reduce(
                          (s, item) => s + parseFloat(item.total_price || 0),
                          0
                        )
                        .toFixed(2)}
                    </Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </Spin>

        {/* Hidden print area */}
        <div style={{ display: "none" }}>
          <div ref={printRef}>
            <VATInvoiceA4
              company={company}
              customer={customer}
              items={vatItems}
              summary={summary}
              taxes={[]}
              invoiceNo={bill?.bill_number || bill?.id}
              invoiceDate={bill?.setup_date || bill?.bill_date}
              invoiceTime={bill?.bill_time}
              watermark={bill?.status === "cancelled" ? "CANCELLED" : "PAID"}
            />
          </div>
        </div>
      </Drawer>
      <ToastContainer />
    </>
  );
}
